import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Trash2, Loader2, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const openAIChat = (sid) => window.dispatchEvent(new CustomEvent('kidsevent-ai-open', { detail: { sid } }));

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export const ChatHistoryList = ({ onOpen, activeSid, compact = false }) => {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/me/ai-chats`, { credentials: 'include' });
      setItems(r.ok ? (await r.json()).items : []);
    } catch { setItems([]); }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('kidsevent-ai-changed', h);
    return () => window.removeEventListener('kidsevent-ai-changed', h);
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Diesen Chat wirklich löschen?')) return;
    await fetch(`${API}/api/ai/session/${id}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
    setItems((cur) => cur.filter((c) => c.id !== id));
    window.dispatchEvent(new CustomEvent('kidsevent-ai-deleted', { detail: { sid: id } }));
    window.dispatchEvent(new Event('kidsevent-ai-changed'));
  };

  if (items === null) return <div className="p-6 text-center text-neutral-500" data-testid="ai-history-loading"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!items.length) {
    return (
      <div className={`text-center text-neutral-500 text-sm ${compact ? 'p-6' : 'bg-white rounded-3xl border border-sky-100 p-10'}`} data-testid="ai-history-empty">
        <MessageSquare className="w-10 h-10 mx-auto text-sky-200 mb-2" />
        Noch keine Chats mit dem KidsEvent Assistenten.
      </div>
    );
  }
  return (
    <ul className={compact ? 'divide-y divide-neutral-200' : 'space-y-3'} data-testid="ai-history-list">
      {items.map((c) => (
        <li key={c.id} className={`flex items-center gap-3 ${compact ? 'px-3 py-2.5' : 'bg-white rounded-2xl border border-sky-100 p-4 shadow-sm'} ${activeSid === c.id ? 'bg-sky-50' : ''}`} data-testid={`ai-history-item-${c.id}`}>
          <button type="button" onClick={() => onOpen(c.id)} className="flex-1 min-w-0 text-left group" data-testid={`ai-history-open-${c.id}`}>
            <div className="text-sm font-semibold text-sky-900 truncate group-hover:text-sky-700">{c.title || 'Chat'}</div>
            {!compact && c.preview && <div className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{c.preview}</div>}
            <div className="text-[11px] text-neutral-400 mt-0.5">{fmt(c.updated_at)} · {c.count} Nachrichten</div>
          </button>
          {!compact && (
            <button type="button" onClick={() => onOpen(c.id)} className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:text-sky-900" data-testid={`ai-history-continue-${c.id}`}>
              <span className="hidden sm:inline">Fortsetzen</span> <ChevronRight className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={() => remove(c.id)} title="Chat löschen" className="p-1.5 rounded text-rose-500 hover:bg-rose-50" data-testid={`ai-history-delete-${c.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </li>
      ))}
    </ul>
  );
};

export const AIChatsTab = () => (
  <div className="space-y-3" data-testid="ai-chats-tab">
    <p className="text-sm text-neutral-600 px-1">Deine Gespräche mit dem KidsEvent Assistenten. Sie bleiben 12 Monate gespeichert und lassen sich jederzeit fortsetzen oder löschen.</p>
    <ChatHistoryList onOpen={openAIChat} />
  </div>
);
